/**
 * Friendly cell text for date fields — shared by the grid, the Excel export and
 * the emailed report table so all three agree by construction.
 *
 * Two bugs this exists to fix, both visible on the report the customer emailed
 * back to us:
 *
 * 1. **Raw ISO in exports.** Date columns formatted themselves with a
 *    `cellRenderer`, which ONLY the on-screen grid runs — AG Grid's Excel export
 *    and `getCellValue({useFormatter:true})` never invoke renderers. So the
 *    attached spreadsheet showed `2027-05-31T07:00:00.000Z`. A `valueFormatter`
 *    is honoured by all three paths, so date columns use `dateValueFormatter`.
 *
 * 2. **Day off by one.** A date-only field is stored as UTC midnight
 *    (`2026-06-30T00:00:00.000Z`); rendered in any US timezone that instant is
 *    the previous evening, so "June 30" printed as "Jun 29". We read the calendar
 *    parts in UTC when the value is midnight UTC (or carries no time at all), and
 *    in local time otherwise — so a genuine timestamp like `createdAt` still
 *    reads as the day it actually happened here. Dates written as local midnight
 *    in a US timezone (`...T07:00:00.000Z`) land on the same calendar day either
 *    way, so they are unaffected.
 */

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const utcFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'
});

const localFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric'
});

/** "Jun 30, 2026" — empty string for anything absent, the original text for anything unparsable. */
export function formatDateCell(value: any): string {
  if (value === null || value === undefined || value === '') return '';

  // A bare "2026-06-30" has no timezone; anchor it to UTC so the parts round-trip.
  if (typeof value === 'string' && DATE_ONLY.test(value.trim())) {
    return utcFormat.format(new Date(`${value.trim()}T00:00:00.000Z`));
  }

  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) {
    // Keep free text ("TBD") visible; drop moment's old sentinel, which is noise.
    const text = String(value).trim();
    return text === 'Invalid date' ? '' : text;
  }

  const isUtcMidnight =
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0;

  return isUtcMidnight ? utcFormat.format(date) : localFormat.format(date);
}

/** AG Grid `valueFormatter` for any date column. */
export function dateValueFormatter(params: any): string {
  return formatDateCell(params?.value);
}
